import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  Plus, Pencil, Trash2, TrendingUp, TrendingDown, RotateCcw, MessageSquarePlus,
  UserPlus, UserMinus, UserCog, PhoneCall, PhoneOff, PhoneIncoming, PhoneMissed,
  Flag, ListChecks, Layers,
  Copy, ArrowRightLeft, FolderInput, Tag, Mail, Phone, Globe, DollarSign,
  Calendar, Bookmark, MessageSquare, AlertCircle, Bell, BellOff, Type,
  ChevronDown, ChevronUp, GitBranch, MinusCircle,
  Video, VideoOff, FileText, RefreshCw, Send, MailOpen, Inbox as InboxIcon,
  Paperclip, AlertTriangle, CheckCircle2, Sparkles, Loader2,
} from "lucide-react";
import { useLeadActivities, useLeadNextAction, useGenerateLeadNextAction } from "../../hooks/useLeads";
import { useTeamMembers, formatMember } from "../../hooks/useTeamMembers";
import { findMemberForActivity } from "../../utils/resolveActivityMember";

// Activities whose detail (e.g. lost reason, long comment) deserves an
// expand/collapse affordance so the timeline doesn't get drowned by a
// paragraph the user wrote three weeks ago.
const SNIPPET_LIMIT = 90;

function getExpandableBody(a) {
  if (a?.type === 'LEAD_LOST') {
    return (a.metadata && a.metadata.lostReason) || null;
  }
  if (a?.type === 'LOST_REASON_UPDATED') {
    return (a.metadata && (a.metadata.to || a.metadata.from)) || null;
  }
  if (a?.type === 'COMMENTS_UPDATED') {
    return (a.metadata && a.metadata.to) || null;
  }
  // A multi-field LEAD_UPDATED that touched Comments carries the new comment
  // text under metadata.comments (or .commentsTo). Surface it so the timeline
  // shows what was actually written, not just "Updated 3 fields: …, Comments".
  if (a?.type === 'LEAD_UPDATED' && a.metadata) {
    return a.metadata.comments || a.metadata.commentsTo || a.metadata.commentTo || null;
  }
  // The description only carries the capped headline sentence; the full
  // 3-line AI summary rides in metadata so it can expand here on demand.
  if (a?.type === 'MEETING_SUMMARY_READY') {
    return (a.metadata && a.metadata.summary) || null;
  }
  return null;
}

function formatRelative(d) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(d).toLocaleDateString();
}

const TYPE_META = {
  LEAD_CREATED:           { icon: Plus,              color: "var(--crm-primary)" },
  LEAD_UPDATED:           { icon: Pencil,            color: "var(--color-text-secondary)" },
  LEAD_DELETED:           { icon: Trash2,            color: "var(--crm-danger)" },
  LEAD_RESTORED:          { icon: RotateCcw,         color: "var(--crm-primary)" },
  LEAD_WON:               { icon: TrendingUp,        color: "var(--crm-success)" },
  LEAD_LOST:              { icon: TrendingDown,      color: "var(--crm-danger)" },
  LEAD_REOPENED:          { icon: RotateCcw,         color: "var(--crm-primary)" },
  LEAD_COPIED_TO:         { icon: Copy,              color: "var(--crm-primary)" },
  LEAD_COPIED_FROM:       { icon: Copy,              color: "var(--crm-primary)" },
  STATUS_CHANGED:         { icon: ArrowRightLeft,    color: "var(--crm-primary)" },
  LEAD_STATUS_CHANGED:    { icon: ArrowRightLeft,    color: "var(--crm-primary)" },
  CO_OWNER_ADDED:         { icon: UserPlus,          color: "var(--crm-primary)" },
  CO_OWNER_REMOVED:       { icon: UserMinus,         color: "var(--color-text-secondary)" },
  // Per-field updates — one type per Lead field so the timeline reads naturally.
  OWNER_CHANGED:          { icon: UserCog,           color: "var(--crm-primary)" },
  PROJECT_CHANGED:        { icon: FolderInput,       color: "var(--crm-primary)" },
  TITLE_UPDATED:          { icon: Type,              color: "var(--color-text-secondary)" },
  BRAND_UPDATED:          { icon: Bookmark,          color: "var(--color-text-secondary)" },
  EMAIL_FIELD_UPDATED:    { icon: Mail,              color: "var(--color-text-secondary)" },
  PHONE_UPDATED:          { icon: Phone,             color: "var(--color-text-secondary)" },
  WEBSITE_UPDATED:        { icon: Globe,             color: "var(--color-text-secondary)" },
  VALUE_UPDATED:          { icon: DollarSign,        color: "var(--crm-success)" },
  CURRENCY_UPDATED:       { icon: DollarSign,        color: "var(--color-text-secondary)" },
  SOURCE_UPDATED:         { icon: Tag,               color: "var(--color-text-secondary)" },
  SOURCE_CHANNEL_UPDATED: { icon: Tag,               color: "var(--color-text-secondary)" },
  NEXT_ACTIVITY_UPDATED:  { icon: Calendar,          color: "var(--color-text-secondary)" },
  EXPECTED_CLOSE_UPDATED: { icon: Calendar,          color: "var(--color-text-secondary)" },
  LABELS_UPDATED:         { icon: Tag,               color: "var(--color-text-secondary)" },
  COMMENTS_UPDATED:       { icon: MessageSquare,     color: "var(--color-text-secondary)" },
  LOST_REASON_UPDATED:    { icon: AlertCircle,       color: "var(--crm-danger)" },
  VENDOR_SEGMENT_UPDATED: { icon: Layers,            color: "var(--color-text-secondary)" },
  NOTE_ADDED:             { icon: MessageSquarePlus, color: "var(--crm-primary)" },
  NOTE_DELETED:           { icon: Trash2,            color: "var(--color-text-secondary)" },
  CONTACT_ADDED:          { icon: UserPlus,          color: "var(--crm-primary)" },
  CONTACT_UPDATED:        { icon: UserCog,           color: "var(--color-text-secondary)" },
  CONTACT_DELETED:        { icon: UserMinus,         color: "var(--crm-danger)" },
  CALL_INITIATED:         { icon: PhoneCall,         color: "var(--crm-primary)" },
  CALL_COMPLETED:         { icon: PhoneCall,         color: "var(--crm-success)" },
  CALL_FAILED:            { icon: PhoneOff,          color: "var(--crm-danger)" },
  CALL_RECEIVED:          { icon: PhoneIncoming,     color: "var(--crm-success)" },
  CALL_MISSED:            { icon: PhoneMissed,       color: "var(--crm-danger)" },
  CALL_NOTE_ADDED:        { icon: MessageSquarePlus, color: "var(--crm-primary)" },
  CALL_DELETED:           { icon: PhoneOff,          color: "var(--crm-danger)" },
  CALL_LOGGED:               { icon: PhoneCall,         color: "var(--crm-primary)" },
  CALL_UPDATED:              { icon: PhoneCall,         color: "var(--color-text-secondary)" },
  // Google Meet meetings.
  MEETING_CREATED:           { icon: Video,             color: "var(--crm-primary)" },
  MEETING_CANCELED:          { icon: VideoOff,          color: "var(--crm-danger)" },
  MEETING_TRANSCRIPT_ADDED:  { icon: FileText,          color: "var(--crm-primary)" },
  MEETING_SUMMARY_READY:     { icon: Sparkles,          color: "var(--crm-success)" },
  MEETING_SYNCED:            { icon: RefreshCw,         color: "var(--crm-primary)" },
  // Email events (Gmail integration).
  EMAIL_SENT:                { icon: Send,              color: "var(--crm-primary)" },
  EMAIL_REPLIED:             { icon: MailOpen,          color: "var(--crm-primary)" },
  EMAIL_RECEIVED:            { icon: InboxIcon,         color: "var(--color-text-secondary)" },
  PIPELINE_MOVE:             { icon: GitBranch,         color: "var(--crm-primary)" },
  PIPELINE_REMOVE:           { icon: MinusCircle,       color: "var(--color-text-secondary)" },
  FOLLOWUP_SCHEDULED:        { icon: Bell,              color: "var(--crm-primary)" },
  FOLLOWUP_COMPLETED:        { icon: Bell,              color: "var(--crm-success)" },
  FOLLOWUP_CLEARED:          { icon: BellOff,           color: "var(--color-text-secondary)" },
  FOLLOWUP_SNOOZED:          { icon: BellOff,           color: "var(--color-text-secondary)" },
  EMAIL_FOLLOWUP_MISSED:     { icon: AlertTriangle,     color: "var(--crm-danger)" },
  EMAIL_FOLLOWUP_ESCALATED:  { icon: AlertTriangle,     color: "var(--crm-danger)" },
  EMAIL_FOLLOWUP_RESOLVED:   { icon: CheckCircle2,      color: "var(--crm-success)" },
  EMAIL_FOLLOWUP_DISMISSED:  { icon: BellOff,           color: "var(--color-text-secondary)" },
  DOCUMENT_UPLOADED:         { icon: Paperclip,         color: "var(--crm-primary)" },
  DOCUMENT_DELETED:          { icon: Trash2,            color: "var(--color-text-secondary)" },
  // Milestones + onboarding — these types were rejected by the Activity enum
  // until 2026-08-17, so no historical rows exist; new ones land here.
  MILESTONE_ADDED:           { icon: Flag,              color: "var(--crm-primary)" },
  MILESTONE_UPDATED:         { icon: Pencil,            color: "var(--color-text-secondary)" },
  MILESTONE_COMPLETED:       { icon: CheckCircle2,      color: "var(--crm-success)" },
  MILESTONE_REOPENED:        { icon: RotateCcw,         color: "var(--crm-primary)" },
  MILESTONE_DELETED:         { icon: Trash2,            color: "var(--color-text-secondary)" },
  ONBOARDING_ADDED:          { icon: ListChecks,        color: "var(--crm-primary)" },
  ONBOARDING_UPDATED:        { icon: Pencil,            color: "var(--color-text-secondary)" },
  ONBOARDING_COMPLETED:      { icon: CheckCircle2,      color: "var(--crm-success)" },
  ONBOARDING_REOPENED:       { icon: RotateCcw,         color: "var(--crm-primary)" },
  ONBOARDING_FINISHED:       { icon: CheckCircle2,      color: "var(--crm-success)" },
  ONBOARDING_DELETED:        { icon: Trash2,            color: "var(--color-text-secondary)" },
};

// Maps an activity to the lead-drawer tab + target item id it points to.
// Returns null if the activity has no navigable target (lead lifecycle events,
// pipeline moves, follow-up snoozes without an id, etc.) or if the id is
// missing on the doc (older rows from before we started persisting metadata
// ids — those simply stay non-clickable).
function navTargetFor(a) {
  if (!a) return null;
  const m = a.metadata || {};
  const type = a.type;
  if (type === 'NOTE_ADDED' || type === 'NOTE_DELETED') {
    return m.noteId ? { tab: 'notes', itemId: String(m.noteId) } : null;
  }
  if (type === 'CONTACT_ADDED' || type === 'CONTACT_UPDATED' || type === 'CONTACT_DELETED') {
    return m.contactId ? { tab: 'contacts', itemId: String(m.contactId) } : null;
  }
  if (
    type === 'CALL_INITIATED' || type === 'CALL_COMPLETED' || type === 'CALL_FAILED' ||
    type === 'CALL_RECEIVED'  || type === 'CALL_MISSED'    ||
    type === 'CALL_NOTE_ADDED'|| type === 'CALL_DELETED'   ||
    type === 'CALL_LOGGED'    || type === 'CALL_UPDATED'
  ) {
    const id = m.callLogId || m.callId;
    return id ? { tab: 'calls', itemId: String(id) } : null;
  }
  if (
    type === 'MEETING_CREATED'  || type === 'MEETING_CANCELED' ||
    type === 'MEETING_TRANSCRIPT_ADDED' || type === 'MEETING_SYNCED' ||
    type === 'MEETING_SUMMARY_READY'
  ) {
    return m.meetingId ? { tab: 'meetings', itemId: String(m.meetingId) } : null;
  }
  if (type === 'EMAIL_SENT' || type === 'EMAIL_REPLIED' || type === 'EMAIL_RECEIVED') {
    return m.emailLinkId ? { tab: 'emails', itemId: String(m.emailLinkId) } : null;
  }
  if (
    type === 'MILESTONE_ADDED'     || type === 'MILESTONE_UPDATED' ||
    type === 'MILESTONE_COMPLETED' || type === 'MILESTONE_REOPENED' ||
    type === 'MILESTONE_DELETED'
  ) {
    return m.milestoneId ? { tab: 'milestones', itemId: String(m.milestoneId) } : null;
  }
  if (type === 'DOCUMENT_UPLOADED' || type === 'DOCUMENT_DELETED') {
    return m.documentId ? { tab: 'files', itemId: String(m.documentId) } : null;
  }
  // FOLLOWUP_* events have no dedicated panel — the follow-up state lives on
  // the lead itself, so there's no row to scroll to. Leave non-clickable.
  return null;
}

export default function ActivityPanel({ leadId, lead, onNavigate }) {
  const { data, isLoading } = useLeadActivities(leadId);

  const comments = (lead?.comments && String(lead.comments).trim()) ? String(lead.comments).trim() : null;
  const items = data?.activities || [];

  if (isLoading) {
    return <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Loading activity…</div>;
  }

  return (
    <div>
      <NextActionCard leadId={leadId} />
      {comments && <CommentsCard text={comments} />}
      {items.length > 0 ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, position: "relative" }}>
          {/* vertical timeline rail */}
          <div style={{
            position: "absolute", left: 11, top: 12, bottom: 12,
            width: 1, background: "var(--color-border-subtle)",
          }} />
          {items.map((a) => <ActivityRow key={a._id} activity={a} onNavigate={onNavigate} />)}
        </ul>
      ) : !comments && (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", textAlign: "center", padding: "24px 0", fontStyle: "italic" }}>
          No activity yet
        </div>
      )}
    </div>
  );
}

// Small AI card pinned above the timeline. Collapsed to a single "Suggest"
// button until first generated; after that shows the headline next action,
// a short rationale, up to 4 supporting checklist items, and how old the
// suggestion is — with a Regenerate button to refresh it on demand.
function NextActionCard({ leadId }) {
  const { data, isLoading } = useLeadNextAction(leadId);
  const generate = useGenerateLeadNextAction(leadId);

  const nextAction = data?.nextAction || "";
  const hasSuggestion = !!nextAction;
  const busy = generate.isPending;

  const onGenerate = async () => {
    try {
      await generate.mutateAsync();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Couldn't generate a suggestion");
    }
  };

  if (isLoading) return null;

  return (
    <div style={{
      marginBottom: 14, borderRadius: 8, padding: "10px 12px",
      border: "1px solid var(--color-border-subtle)",
      background: hasSuggestion ? "var(--crm-primary-soft)" : "var(--color-bg-base)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{
          width: 24, height: 24, borderRadius: 9999, flexShrink: 0, marginTop: 1,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: hasSuggestion ? "var(--crm-primary)" : "var(--color-bg-muted)",
          color: hasSuggestion ? "#fff" : "var(--color-text-secondary)",
        }}>
          <Sparkles size={12} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {!hasSuggestion ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-primary)" }}>
                  Next suggested action
                </div>
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 1 }}>
                  {generate.isError ? "Couldn't generate — try again" : "AI can recommend what to do next on this lead"}
                </div>
              </div>
              <button
                type="button"
                onClick={onGenerate}
                disabled={busy}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
                  padding: "6px 12px", borderRadius: 8, border: "none",
                  background: "var(--crm-primary)", color: "#fff",
                  fontSize: 11, fontWeight: 600, cursor: busy ? "wait" : "pointer",
                  opacity: busy ? 0.75 : 1,
                }}
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                {busy ? "Thinking…" : "Suggest"}
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", lineHeight: 1.4 }}>
                  {nextAction}
                </div>
                <button
                  type="button"
                  onClick={onGenerate}
                  disabled={busy}
                  title="Regenerate"
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 24, height: 24, borderRadius: 8, flexShrink: 0,
                    border: "1px solid var(--crm-primary)", background: "transparent",
                    color: "var(--crm-primary)", cursor: busy ? "wait" : "pointer",
                  }}
                >
                  {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                </button>
              </div>

              {data?.summary && (
                <div style={{ fontSize: 12, color: "var(--color-text-primary)", lineHeight: 1.5, marginTop: 4 }}>
                  {data.summary}
                </div>
              )}

              {Array.isArray(data?.actionItems) && data.actionItems.length > 0 && (
                <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                  {data.actionItems.slice(0, 4).map((item, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, color: "var(--color-text-primary)", lineHeight: 1.4 }}>
                      <ListChecks size={11} style={{ color: "var(--crm-primary)", flexShrink: 0, marginTop: 2 }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 6 }}>
                {data?.generatedAt ? `Generated ${formatRelative(data.generatedAt)}` : ""}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Pinned card showing the lead's current `comments` field — the running
// free-text notes that older CRM data kept on the lead itself (and which the
// activity feed only references as "Updated … Comments"). Collapses long text.
function CommentsCard({ text }) {
  const isLong = text.length > 220;
  const [expanded, setExpanded] = useState(false);
  const shown = isLong && !expanded ? `${text.slice(0, 220)}…` : text;
  return (
    <div style={{
      marginBottom: 16,
      border: "1px solid var(--color-border-subtle)",
      background: "var(--color-bg-surface)",
      borderRadius: 8,
      padding: "12px 14px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <MessageSquare size={13} style={{ color: "var(--crm-primary)" }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>
          Comments
        </span>
      </div>
      <div style={{ fontSize: 13, color: "var(--color-text-primary)", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {shown}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 2, marginTop: 6,
            background: "transparent", border: "none", padding: 0, cursor: "pointer",
            color: "var(--crm-primary)", fontSize: 12, fontWeight: 500,
          }}
        >
          {expanded ? <>Show less <ChevronUp size={11} /></> : <>Show more <ChevronDown size={11} /></>}
        </button>
      )}
    </div>
  );
}

function ActivityRow({ activity: a, onNavigate }) {
  const meta = TYPE_META[a.type] || { icon: Pencil, color: "var(--color-text-secondary)" };
  const Icon = meta.icon;
  const body = getExpandableBody(a);
  const isLong = body && body.length > SNIPPET_LIMIT;
  const [expanded, setExpanded] = useState(false);
  const { byId, members } = useTeamMembers();
  const member = findMemberForActivity(a, { byId, members });
  const displayName = member
    ? (member.name || formatMember(member))
    : (a.userEmail || "");
  const titleEmail = a.user?.email || a.userEmail || undefined;
  const navTarget = onNavigate ? navTargetFor(a) : null;
  const clickable = !!navTarget;
  const onRowClick = clickable ? () => onNavigate(navTarget.tab, navTarget.itemId) : undefined;
  const onRowKey = clickable
    ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(navTarget.tab, navTarget.itemId); } }
    : undefined;

  return (
    <li style={{ display: "flex", gap: 8, padding: "5px 0", position: "relative" }}>
      <div style={{
        width: 22, height: 22, borderRadius: 9999, flexShrink: 0, zIndex: 1,
        background: "var(--color-bg-base)",
        border: `1px solid var(--color-border-subtle)`,
        color: meta.color,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={11} />
      </div>
      <div
        onClick={onRowClick}
        onKeyDown={onRowKey}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        title={clickable ? `Open in ${navTarget.tab} tab` : undefined}
        style={{
          flex: 1, minWidth: 0, paddingTop: 2,
          cursor: clickable ? "pointer" : "default",
          borderRadius: 6,
          padding: clickable ? "2px 6px 2px 4px" : "2px 4px",
          margin: clickable ? "-2px -6px -2px -4px" : "-2px -4px",
          transition: "background 120ms ease",
        }}
        onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; } : undefined}
        onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = "transparent"; } : undefined}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
          <span style={{ fontSize: 12.5, color: "var(--color-text-primary)", lineHeight: "16px" }}>
            {a.description}
            {displayName && (
              <span style={{ color: "var(--color-text-secondary)", fontWeight: 400 }}> · {displayName}</span>
            )}
          </span>
          <span style={{ fontSize: 10.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap", flexShrink: 0 }} title={titleEmail}>
            {formatRelative(a.createdAt)}
          </span>
        </div>

        {body && (
          <div style={{
            marginTop: 4,
            padding: "6px 8px",
            borderRadius: 6,
            background: a.type === 'LEAD_LOST' || a.type === 'LOST_REASON_UPDATED'
              ? "var(--crm-danger-soft)"
              : "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            fontSize: 12.5,
            color: "var(--color-text-primary)",
            lineHeight: 1.45,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
            {isLong && !expanded ? `${body.slice(0, SNIPPET_LIMIT)}…` : body}
            {isLong && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 2,
                  marginLeft: 6,
                  background: "transparent", border: "none", padding: 0,
                  cursor: "pointer",
                  color: "var(--crm-primary)",
                  fontSize: 12, fontWeight: 500,
                }}
              >
                {expanded ? <>Show less <ChevronUp size={11} /></> : <>Show more <ChevronDown size={11} /></>}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
